import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Textarea } from '@1trading/react/components/ui/textarea';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BINARY_ANSWERS } from '@/constants/answers';
import { LICENCE_COPY_SOURCES, LicenceCopySource } from '@/constants/institution';
import { InstitutionFieldDocument } from '@/features/institution/components/fields/document';
import { useInstitutionDetailsLocked } from '@/features/institution/hooks/useInstitutionDetails';
import type { InstitutionRegulatoryInformationOrDefaults } from '@/features/institution/regulatory-information/schema';
import { DocumentSource } from '@/services/api/documents';

const InstitutionRegulatoryInformationFieldsetLicence = () => {
  const form = useFormContext<InstitutionRegulatoryInformationOrDefaults>();
  const { locked } = useInstitutionDetailsLocked();
  const { t } = useTranslation();
  const licenceCopySource = form.watch('licenceCopySource');
  const isLicenceDetailsLocked = locked.regulatoryInformation?.licenseOrAuthorisationRegistrationDetails !== undefined;

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="licenseOrAuthorisationRegistrationDetails"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.regulatoryInformation.licenseOrAuthorisationRegistrationDetails')}*</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                disabled={isLicenceDetailsLocked}
                placeholder={t('institutionDetails.regulatoryInformation.placeholders.licenseOrAuthorisationRegistrationDetails')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="isMifidInvestmentFirm"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.regulatoryInformation.isMifidInvestmentFirm')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.regulatoryInformation.isMifidInvestmentFirm')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="licenceCopySource"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.regulatoryInformation.licenceCopySource.title')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.regulatoryInformation.licenceCopySource.title')}
                onValueChange={field.onChange}
              >
                {LICENCE_COPY_SOURCES.map((source) => (
                  <FormItem key={source}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={source} />
                      </FormControl>
                      <FormLabel contained>{t(`institutionDetails.regulatoryInformation.licenceCopySource.options.${source}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {licenceCopySource === LicenceCopySource.DOCUMENT ? (
        <InstitutionFieldDocument<InstitutionRegulatoryInformationOrDefaults>
          label={t('institutionDetails.regulatoryInformation.licenceDocument')}
          name="licenceDocument"
          source={DocumentSource.LICENCES_AND_POLICIES}
        />
      ) : null}
      {licenceCopySource === LicenceCopySource.URL ? (
        <FormField
          control={form.control}
          name="licenceUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.regulatoryInformation.licenceUrl')}*</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t('institutionDetails.regulatoryInformation.placeholders.licenceUrl')} inputMode="url" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </div>
  );
};
InstitutionRegulatoryInformationFieldsetLicence.displayName = 'InstitutionRegulatoryInformationFieldsetLicence';

export { InstitutionRegulatoryInformationFieldsetLicence };
